/*
 * What the cable builder may offer right now. The server sends the
 * connectors the owner has switched on and the kinds of build that are left
 * (GET /storefront/cable-builder/availability); this prunes the public
 * catalogue the same way the server does (app/lib/cable-builder/availability.ts,
 * effectiveCatalog), so every list, start tile and option shows only what
 * can be ordered. A parity test keeps the two in step.
 *
 * Plain ES5, no build step. window.CableAvailability in the browser,
 * module.exports for the test.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === "object" && module && module.exports)
    module.exports = api;
  else root.CableAvailability = api;
})(typeof self !== "undefined" ? self : this, function () {
  "use strict";

  var keys = Object.keys;
  /** app/lib/cable-builder/free-design.ts FREE_DESIGN_ID */
  var FREE_DESIGN_ID = 100;

  function setOf(ids) {
    var s = {};
    for (var i = 0; i < ids.length; i++) s[String(ids[i])] = true;
    return s;
  }
  function size(s) {
    return keys(s).length;
  }
  function isSwitchable(part) {
    return !!(part && part.look);
  }
  function byNumber(a, b) {
    return a - b;
  }
  function assign(target, source) {
    keys(source).forEach(function (k) {
      target[k] = source[k];
    });
    return target;
  }

  /** The launch range, for when the server cannot be asked. */
  function launch(catalog) {
    var parts = [];
    keys(catalog.parts).forEach(function (k) {
      var p = catalog.parts[k];
      if (isSwitchable(p) && p.launch) parts.push(p.id);
    });
    return {
      ok: true,
      parts: parts.sort(byNumber),
      assemblies: null,
      tiers: null,
      fallback: true,
    };
  }

  function copyStep(step) {
    var sets = {};
    keys(step.sets).forEach(function (sid) {
      sets[sid] = step.sets[sid].slice();
    });
    return {
      dependsOn: step.dependsOn,
      map: assign({}, step.map),
      sets: sets,
    };
  }

  function keyedByFirst(a, step) {
    var cableKey = a.layout && a.layout.cableKey;
    return (
      step.dependsOn === "first" || !cableKey || cableKey === a.defaultStep
    );
  }

  function offeredCables(a) {
    var cableKey = a.layout && a.layout.cableKey;
    if (!cableKey) return {};
    if (cableKey === a.defaultStep) return setOf(a.first);
    var step = a.compat[cableKey];
    var out = {};
    if (step)
      keys(step.sets).forEach(function (sid) {
        assign(out, setOf(step.sets[sid]));
      });
    return out;
  }

  function signature(a) {
    return JSON.stringify([a.first, a.compat]);
  }

  function prune(source, allowed) {
    var a = assign({}, source);
    a.first = source.first.filter(allowed);
    a.compat = {};
    keys(source.compat).forEach(function (step) {
      a.compat[step] = copyStep(source.compat[step]);
    });
    var steps = keys(a.compat);
    steps.forEach(function (step) {
      var c = a.compat[step];
      keys(c.sets).forEach(function (sid) {
        c.sets[sid] = c.sets[sid].filter(allowed);
      });
      keys(c.map).forEach(function (pid) {
        var set = c.sets[c.map[pid]];
        if (!allowed(Number(pid)) || !(set && set.length)) delete c.map[pid];
      });
    });

    var cableKey = (a.layout && a.layout.cableKey) || null;
    for (var pass = 0; pass < 4; pass += 1) {
      var before = signature(a);
      var cableSteps = steps.filter(function (step) {
        return a.compat[step].dependsOn === "cable";
      });
      if (cableKey && cableKey !== a.defaultStep && cableSteps.length) {
        var usable = null;
        cableSteps.forEach(function (step) {
          var ids = setOf(keys(a.compat[step].map));
          if (usable === null) usable = ids;
          else
            keys(usable).forEach(function (id) {
              if (!ids[id]) delete usable[id];
            });
        });
        var cables = a.compat[cableKey];
        if (cables) {
          keys(cables.sets).forEach(function (sid) {
            cables.sets[sid] = cables.sets[sid].filter(function (id) {
              return usable[String(id)] === true;
            });
          });
          keys(cables.map).forEach(function (pid) {
            var set = cables.sets[cables.map[pid]];
            if (!(set && set.length)) delete cables.map[pid];
          });
        }
      }
      var firstSteps = steps.filter(function (step) {
        return keyedByFirst(a, a.compat[step]);
      });
      a.first = a.first.filter(function (id) {
        return firstSteps.every(function (step) {
          return a.compat[step].map[id] !== undefined;
        });
      });
      if (signature(a) === before) break;
    }

    steps.forEach(function (step) {
      var c = a.compat[step];
      var used = {};
      keys(c.map).forEach(function (pid) {
        used[String(c.map[pid])] = true;
      });
      keys(c.sets).forEach(function (sid) {
        if (!used[sid]) delete c.sets[sid];
      });
    });
    if (!a.first.length) return null;
    if (cableKey && cableKey !== a.defaultStep && !size(offeredCables(a)))
      return null;

    if (a.layout && a.layout.cableChoices) {
      var offered = offeredCables(a);
      a.layout = assign({}, a.layout);
      a.layout.cableChoices = a.layout.cableChoices
        .map(function (choice) {
          var next = assign({}, choice);
          next.ids = choice.ids.filter(function (id) {
            return offered[String(id)] === true;
          });
          return next;
        })
        .filter(function (choice) {
          return choice.ids.length;
        });
    }
    return a;
  }

  /** The catalogue with switched-off connectors and kinds of build taken out. */
  function apply(catalog, availability) {
    var on = setOf(availability.parts || []);
    var listed = availability.assemblies
      ? setOf(availability.assemblies)
      : null;
    function allowed(id) {
      var p = catalog.parts[String(id)];
      return !p || !isSwitchable(p) || on[String(id)] === true;
    }
    var assemblies = [];
    var removed = [];
    catalog.assemblies.forEach(function (source) {
      var pruned =
        listed && !listed[String(source.id)] ? null : prune(source, allowed);
      if (pruned) assemblies.push(pruned);
      else removed.push(source.id);
    });
    if (availability.freeDesign === false) removed.push(FREE_DESIGN_ID);
    var withheld = [];
    keys(catalog.parts).forEach(function (k) {
      var p = catalog.parts[k];
      if (isSwitchable(p) && !on[String(p.id)]) withheld.push(p.id);
    });
    var out = assign({}, catalog);
    out.assemblies = assemblies;
    out.withheld = { parts: withheld, assemblies: removed };
    return out;
  }

  function designRefs(value, path, out) {
    if (!value || typeof value !== "object" || Array.isArray(value)) return out;
    if (typeof value.id === "number" && path.length)
      out.push({ id: value.id, field: path.join(".") });
    keys(value).forEach(function (k) {
      var inner = value[k];
      if (inner && typeof inner === "object")
        designRefs(inner, path.concat([k]), out);
    });
    return out;
  }

  function noun(part) {
    if (part.kind === "stagebox") return "stage box";
    if (part.kind === "panelmultipin") return "multipin";
    return "connector";
  }

  /**
   * Why a saved design can no longer be ordered, in the server's words, or
   * null. `catalog` is one `apply` returned.
   */
  function unavailable(catalog, item) {
    var withheld = catalog && catalog.withheld;
    if (!withheld || !item) return null;
    if (withheld.assemblies.indexOf(item.assembly) !== -1)
      return {
        field: "assembly",
        message:
          "That kind of build is no longer available. Remove it from your basket, or choose another kind of build.",
      };
    var off = setOf(withheld.parts);
    var refs = designRefs(item.design, [], []);
    for (var i = 0; i < refs.length; i++) {
      if (!off[String(refs[i].id)]) continue;
      var p = catalog.parts[String(refs[i].id)];
      var name = (p && ((p.look && p.look.title) || p.name)) || "that part";
      return {
        field: refs[i].field,
        message:
          "That " +
          (p ? noun(p) : "connector") +
          " is no longer available (" +
          name +
          "). Choose another one.",
      };
    }
    return null;
  }

  return {
    apply: apply,
    launch: launch,
    unavailable: unavailable,
    FREE_DESIGN_ID: FREE_DESIGN_ID,
  };
});
